import React, { useState } from 'react'
import { BookOpen, Pencil, Trash2 } from 'lucide-react'
import MarkdownEditor from './MarkdownEditor'
import AddComposantButton from './AddComposantButton'
import clsx from 'clsx'

export type ComposantData = {
  id: string
  name: string
  description: string,
  images?: string[]
}

type ComposantSectionProps = {
  composants: ComposantData[]
  onChange: (composants: ComposantData[]) => void
  title?: string
  emptyLabel?: string
  addLabel?: string
  className?: string
  editable?: boolean
}

export const ComposantSection: React.FC<ComposantSectionProps> = ({
  composants,
  onChange,
  title = 'Composants',
  emptyLabel = 'Aucun composant pour le moment.',
  addLabel = 'Ajouter un composant',
  className = '',
  editable = true,
}) => {
  const [adding, setAdding] = useState(false)
  const [newName, setNewName] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editNameId, setEditNameId] = useState<string | null>(null)

  // Add new composant
  const handleAdd = () => {
    if (!newName.trim()) return
    let v = newName.trim();
    v = filterLettersOnly(v);
    v = capitalizeFirstLetter(v);
    const newComp: ComposantData = {
      id: Math.random().toString(36).slice(2, 10),
      name: v,
      description: '',
    }
    onChange([...composants, newComp])
    setNewName('')
    setAdding(false)
    setSelectedId(newComp.id)
    setEditNameId(null)
  }

  // Delete composant
  const handleDelete = (id: string) => {
    onChange(composants.filter((c) => c.id !== id))
    if (selectedId === id) setSelectedId(null)
    if (editNameId === id) setEditNameId(null)
  }

  function filterLettersOnly(str) {
    return str.replace(/[^a-zA-Z]/g, '');
  }

  function capitalizeFirstLetter(str) {
    if (str.length === 0) return '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Update name
  const handleNameChange = (id: string, name: string) => {
    let v = name;
    v = filterLettersOnly(v);
    v = capitalizeFirstLetter(v);
    onChange(
      composants.map((c) => (c.id === id ? { ...c, name: v } : c)),
    )
  }

  // Update description
  const handleDescChange = (id: string, desc: string) => {
    onChange(
      composants.map((c) => (c.id === id ? { ...c, description: desc } : c)),
    )
  }

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = () => {
      const imageUrl = reader.result as string
      onChange(
        composants.map((c) =>
          c.id === id
            ? {
              ...c,
              images: [...(c.images || []), imageUrl],
            }
            : c,
        ),
      )
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = (id: string, index: number) => {
    onChange(
      composants.map((c) =>
        c.id === id
          ? {
            ...c,
            images: (c.images || []).filter((_, i) => i !== index),
          }
          : c,
      ),
    )
  }

  return (
    <section className={clsx('card p-4 md:p-6 flex flex-col gap-4', className)}>
      <div className="flex items-center gap-2 mb-2">
        <BookOpen className="w-5 h-5 text-yellow-brixel" />
        <span className="font-semibold text-gray-900">{title}</span>
        {editable && (
          <AddComposantButton
            onClick={() => setAdding(true)}
            className="ml-auto px-3 py-1 text-sm"
          >
            {addLabel}
          </AddComposantButton>
        )}
      </div>
      {composants.length === 0 && !adding && (
        <div className="text-gray-400 italic text-sm px-2 py-1">{emptyLabel}</div>
      )}
      <ul className="space-y-3">
        {composants.map((comp) => (
          <li
            key={comp.id}
            className={clsx(
              'rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 relative group transition',
              selectedId === comp.id && 'ring-2 ring-brixel/30 bg-white',
            )}
          >
            <div className="flex items-center gap-2 mb-1">
              {editNameId === comp.id ? (
                <input
                  className="bg-transparent font-medium text-gray-800 px-0.5 py-0.5 border-b border-brixel/40 focus:border-brixel focus:outline-none rounded transition w-auto max-w-[160px] text-base"
                  value={comp.name}
                  onChange={(e) => handleNameChange(comp.id, e.target.value)}
                  onBlur={() => setEditNameId(null)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Escape') setEditNameId(null)
                  }}
                  maxLength={40}
                  autoFocus
                  aria-label="Nom du composant"
                  disabled={!editable}
                />
              ) : (
                <span
                  className={clsx(
                    'font-medium text-gray-800 flex-1 truncate',
                    !comp.name && 'italic text-gray-400',
                  )}
                  tabIndex={0}
                  onClick={() => editable && setEditNameId(comp.id)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && editable) setEditNameId(comp.id)
                  }}
                  role={editable ? 'button' : undefined}
                  aria-label="Modifier le nom"
                  title={editable ? 'Cliquer pour éditer le nom' : undefined}
                >
                  {comp.name || 'Nom du composant'}
                </span>
              )}
              {editable && (
                <>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-brixel/10 text-brixel"
                    title="Éditer la description"
                    onClick={() => setSelectedId(comp.id)}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-red-100 text-red-500"
                    title="Supprimer le composant"
                    onClick={() => handleDelete(comp.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
            {/* Description markdown */}
            {selectedId === comp.id && editable ? (
              <div className="mt-1">
                <MarkdownEditor
                  value={comp.description}
                  onChange={(desc) => handleDescChange(comp.id, desc)}
                  placeholder="Décrivez ce composant (Markdown)…"
                  minRows={3}
                  maxRows={10}
                  autoFocus
                />
                <div className="mt-4">
                  <label className="text-sm font-medium text-gray-700">Images</label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(comp.images || []).map((url, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={url}
                          alt={`img-${index}`}
                          className="w-24 h-24 object-cover rounded border border-gray-200"
                        />
                        <button
                          type="button"
                          title="Supprimer"
                          onClick={() => handleRemoveImage(comp.id, index)}
                          className="absolute top-1 right-1 bg-white/80 rounded-full p-1 shadow hover:bg-red-500 hover:text-white transition text-sm text-gray-600"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <label className="w-24 h-24 flex items-center justify-center border border-dashed border-gray-300 rounded cursor-pointer hover:bg-gray-100 text-sm text-gray-500">
                      +
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => handleImageUpload(e, comp.id)}
                      />
                    </label>
                  </div>
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    type="button"
                    className="btn-outline flex-1"
                    onClick={() => setSelectedId(null)}
                  >
                    Fermer
                  </button>
                </div>
              </div>
            ) : comp.description ? (
              <div className="markdown text-sm mt-1">{comp.description}</div>
            ) : (
              <div className="text-gray-400 italic text-xs mt-1">Pas de description.</div>
            )}
          </li>
        ))}
      </ul>
      {adding && editable && (
        <div className="mt-3">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <input
                className="border border-gray-200 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-brixel flex-1"
                placeholder="Nom du composant…"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={40}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd()
                  if (e.key === 'Escape') {
                    setAdding(false)
                    setNewName('')
                  }
                }}
                disabled={!editable}
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn px-3 py-1 text-sm flex-1"
                onClick={handleAdd}
                disabled={!newName.trim()}
              >
                Ajouter
              </button>
              <button
                type="button"
                className="btn-outline px-3 py-1 text-sm flex-1"
                onClick={() => {
                  setAdding(false)
                  setNewName('')
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

export default ComposantSection
